import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Grid, Paper, Button, Stack, Link, CircularProgress, LinearProgress, Alert,
    Tooltip, TextField
} from "@mui/material";
import { getDashboardStats } from '../../Services/Dashboard/DashboardApis';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';

const RetreatDashboard = () => {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const fetchData = useCallback(async (start = startDate, end = endDate) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getDashboardStats(start, end);
            if (response && response.success && response.data) {
                setDashboardData(response.data);
            } else if (response && response.summary) {
                setDashboardData(response);
            } else if (response && response.data) {
                setDashboardData(response.data);
            } else {
                setDashboardData(response || null);
            }
        } catch (err) {
            const errMsg = err.response?.data?.message || err.message || 'Failed to fetch dashboard data';
            setError(errMsg);
            console.error('Error fetching dashboard data:', err);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData("", "");
    }, [fetchData]);

    const handleFilter = () => {
        fetchData(startDate, endDate);
    };

    const handleReset = () => {
        setStartDate("");
        setEndDate("");
        fetchData("", "");
    };

    // Extract API data with fallbacks matching specifications
    const kpiCards = dashboardData?.kpiCards || {};
    const attention = dashboardData?.needsYourAttention || {};
    const chartData = dashboardData?.gmvAndCommissionChart || [];
    const bookingStates = dashboardData?.bookingStates || {};
    const latestBookings = dashboardData?.latestBookings || [];
    const topRevenueRetreats = dashboardData?.topRevenueRetreats || [];
    const period = dashboardData?.period || "1–28 July 2026";

    // Helpers & Calculations
    const formatCurrencyDisplay = (val) => {
        const num = parseFloat(val || 0);
        if (isNaN(num) || num === 0) return '₹0';
        if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
        if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
        return `₹${num.toLocaleString('en-IN')}`;
    };

    const getStatusBadge = (status) => {
        const s = (status || 'pending').toLowerCase();
        if (s === 'confirmed') {
            return { label: 'Confirmed', bg: '#E3F2FD', color: '#1976D2' };
        }
        if (s === 'completed') {
            return { label: 'Completed', bg: '#E8F5E8', color: '#2E7D32' };
        }
        if (s === 'cancelled') {
            return { label: 'Cancelled', bg: '#FFEBEE', color: '#C62828' };
        }
        if (s === 'rejected') {
            return { label: 'Rejected', bg: '#FFEBEE', color: '#B71C1C' };
        }
        if (s === 'awaiting_host_confirmation') {
            return { label: 'Awaiting Host', bg: '#FFF4E5', color: '#ED6C02' };
        }
        if (s === 'awaiting_admin_confirmation') {
            return { label: 'Awaiting Admin', bg: '#F3E5F5', color: '#9C27B0' };
        }
        if (s === 'date_change_requested') {
            return { label: 'Date Change Req', bg: '#E1F5FE', color: '#0288D1' };
        }
        if (s === 'date_change_approved') {
            return { label: 'Date Change Appr', bg: '#E0F2F1', color: '#00897B' };
        }
        if (s === 'date_change_rejected') {
            return { label: 'Date Change Rej', bg: '#FCE4EC', color: '#D81B60' };
        }
        if (s === 'user_cancelled_date_change_request') {
            return { label: 'Cancelled DC', bg: '#ECEFF1', color: '#546E7A' };
        }
        if (s === 'awaiting_payment' || s === 'awaiting payment' || s === 'awaiting pay') {
            return { label: 'Awaiting Pay', bg: '#FFF8E1', color: '#F57F17' };
        }
        return { label: 'Pending', bg: '#FFF8E1', color: '#F57C00' };
    };

    const displayBookings = latestBookings.slice(0, 5);
    const displayTopRetreats = topRevenueRetreats.slice(0, 5);

    const maxTopRev = displayTopRetreats.length > 0
        ? Math.max(...displayTopRetreats.map(r => parseFloat(r.totalRevenue || 0)))
        : 1;
    const safeMaxTopRevenue = maxTopRev > 0 ? maxTopRev : 1;
    const barColors = ['#6D3917', '#D97706', '#557A46', '#4F6F52', '#C88EA7'];

    // Calculate max value for GMV chart bars scaling
    const rawMaxGmv = chartData.length > 0 ? Math.max(...chartData.map(d => d.gmv || 0)) : 2000000;
    const maxGmv = rawMaxGmv > 0 ? rawMaxGmv : 1;

    // Total bookings for booking states progress bar percentage calculation
    const totalStateBookings = (bookingStates.pending || 0) + (bookingStates.confirmed || 0) + (bookingStates.completed || 0) +
        (bookingStates.cancelled || 0);

    const todayStr = new Date().toISOString().split('T')[0];

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { md: 3 }, minHeight: "100vh" }}>
            {/* Top Header */}
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Typography sx={{
                        color: '#222',
                        fontSize: { xs: '1.75rem', md: '1.8rem' },
                        fontWeight: 500
                    }}>
                        Dashboard
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.85rem', mt: 0.3 }}>
                        Marketplace at a glance · {period}
                    </Typography>
                </Box>

                {/* Filter & Export Action Toolbar */}
                <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
                    <TextField
                        size="small"
                        type="date"
                        label="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: endDate || todayStr }}
                        sx={{
                            width: { xs: '100%', sm: 155 },
                            bgcolor: '#fff',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                '& fieldset': { borderColor: '#E0E0E0' },
                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65' }
                            }
                        }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: todayStr, min: startDate }}
                        sx={{
                            width: { xs: '100%', sm: 155 },
                            bgcolor: '#fff',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                '& fieldset': { borderColor: '#E0E0E0' },
                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65' }
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleFilter}
                        disableElevation
                        sx={{
                            bgcolor: '#FFBD65',
                            color: '#fff',
                            textTransform: 'none',
                            borderRadius: '6px',
                            px: 2.5,
                            height: '40px',
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            '&:hover': { bgcolor: '#F5A93B' }
                        }}
                    >
                        Filter
                    </Button>
                    {(startDate || endDate) && (
                        <Button
                            variant="outlined"
                            onClick={handleReset}
                            sx={{
                                color: '#666',
                                borderColor: '#E0E0E0',
                                textTransform: 'none',
                                borderRadius: '6px',
                                px: 2,
                                height: '40px',
                                fontSize: '0.85rem',
                                '&:hover': { borderColor: '#BDBDBD' }
                            }}
                        >
                            Reset
                        </Button>
                    )}
                </Stack>
            </Stack>

            {loading && !dashboardData && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={32} sx={{ color: '#FFBD65' }} />
                </Box>
            )}

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '6px' }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Top KPI Cards Section with Separator Border */}
            <Box sx={{ pb: 3, mb: 3, borderBottom: '1px solid #E0E0E0' }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(1, 1fr)',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(5, 1fr)',
                        lg: 'repeat(7, 1fr)'
                    },
                    gap: 1
                }}>
                    {/* 1. GMV */}
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Typography sx={{ color: '#888', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                            GMV
                        </Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#222', lineHeight: 1.1, mb: 0.8 }}>
                            {formatCurrencyDisplay(kpiCards.gmv?.value ?? 232787)}
                        </Typography>
                        {/* <Typography sx={{ color: '#4CAF50', fontSize: '0.75rem', fontWeight: 600 }}>
                            {kpiCards.gmv?.growthText || "▲ 153% vs last month"}
                        </Typography> */}
                    </Paper>

                    {/* 2. NET REVENUE */}
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Typography sx={{ color: '#888', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                            NET REVENUE
                        </Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#4CAF50', lineHeight: 1.1, mb: 0.8 }}>
                            {formatCurrencyDisplay(kpiCards.netRevenue?.value ?? 58317)}
                        </Typography>
                        {/* <Typography sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 500 }}>
                            {kpiCards.netRevenue?.subtext || "commission · 20% avg"}
                        </Typography> */}
                    </Paper>

                    {/* 3. HOST REVENUE */}
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Typography sx={{ color: '#888', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                            HOST REVENUE
                        </Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#8B4513', lineHeight: 1.1, mb: 0.8 }}>
                            {formatCurrencyDisplay(kpiCards.hostRevenue?.value ?? 174470)}
                        </Typography>
                        {/* <Typography sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 500 }}>
                            payout to hosts
                        </Typography> */}
                    </Paper>

                    {/* 4. BOOKINGS */}
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Typography sx={{ color: '#888', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                            BOOKINGS
                        </Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#222', lineHeight: 1.1, mb: 0.8 }}>
                            {kpiCards.bookings?.value ?? 23}
                        </Typography>
                        {/* <Typography sx={{ color: '#4CAF50', fontSize: '0.75rem', fontWeight: 600 }}>
                            {kpiCards.bookings?.subtext || "▲ 5 this week"}
                        </Typography> */}
                    </Paper>

                    {/* 5. ENQUIRIES */}
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Typography sx={{ color: '#888', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                            ENQUIRIES
                        </Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#FF9800', lineHeight: 1.1, mb: 0.8 }}>
                            {kpiCards.enquiries?.value ?? 0}
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 500 }}>
                            {`${kpiCards.enquiries?.unread || 0} unread · ${kpiCards.enquiries?.read || 0} read`}
                        </Typography>
                    </Paper>

                    {/* JOIN WITH US */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            border: '1px solid #E8E8E8',
                            bgcolor: '#FFFFFF',
                            height: '100%',
                        }}
                    >
                        <Typography sx={{ color: '#888', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                            JOIN WITH US
                        </Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#1565C0', lineHeight: 1.1, mb: 0.8 }}>
                            {kpiCards.joinWithUs?.value ?? 0}
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 500 }}>
                            {`${kpiCards.joinWithUs?.pending || 0} pending · ${kpiCards.joinWithUs?.contacted || 0} contacted`}
                        </Typography>
                    </Paper>

                    {/* 6. LIVE RETREATS */}
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Typography sx={{ color: '#888', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                            LIVE RETREATS
                        </Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#222', lineHeight: 1.1, mb: 0.8 }}>
                            {kpiCards.liveRetreats?.value ?? 3}
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 500 }}>
                            {kpiCards.liveRetreats?.subtext || "0 awaiting approval"}
                        </Typography>
                    </Paper>

                    {/* 7. ACTIVE HOSTS */}
                    {/* <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Typography sx={{ color: '#888', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                            ACTIVE HOSTS
                        </Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#222', lineHeight: 1.1, mb: 0.8 }}>
                            {kpiCards.activeHosts?.value ?? 2}
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 500 }}>
                            {kpiCards.activeHosts?.subtext || "0 pending KYC"}
                        </Typography>
                    </Paper> */}
                </Box>
            </Box>

            {/* NEEDS YOUR ATTENTION Box */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: '8px', bgcolor: '#FFF9E6', border: '1px solid #FFE8B5' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Typography sx={{ color: '#D97706', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                        NEEDS YOUR ATTENTION
                    </Typography>
                    <Box sx={{ bgcolor: '#FFE8B5', color: '#D97706', fontSize: '0.65rem', fontWeight: 700, px: 1, py: 0.2, borderRadius: '4px' }}>
                        NEW
                    </Box>
                </Stack>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
                    {/* Badge 1: Retreats to approve */}
                    <Box
                        onClick={() => navigate('/retreat')}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8,
                            bgcolor: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: '6px', cursor: 'pointer',
                            transition: 'all 0.2s', '&:hover': { borderColor: '#FFBD65', bgcolor: '#FFFDF9' }
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#E65100' }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                            <strong style={{ color: '#E65100' }}>{attention.retreatsToApprove ?? 3}</strong> retreats to approve
                        </Typography>
                    </Box>

                    {/* Badge 2: Refunds to review */}
                    <Box
                        onClick={() => navigate('/bookings/refund-requests')}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8,
                            bgcolor: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: '6px', cursor: 'pointer',
                            transition: 'all 0.2s', '&:hover': { borderColor: '#FFBD65', bgcolor: '#FFFDF9' }
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#E65100' }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                            <strong style={{ color: '#E65100' }}>{attention.refundsToReview ?? 2}</strong> refunds to review
                        </Typography>
                    </Box>

                    {/* Badge 3: Awaiting host confirmation */}
                    {/* <Box
                        onClick={() => navigate('/bookings')}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8,
                            bgcolor: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: '6px', cursor: 'pointer',
                            transition: 'all 0.2s', '&:hover': { borderColor: '#FFBD65', bgcolor: '#FFFDF9' }
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FF9800' }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                            <strong>{attention.awaitingHostConfirmation ?? 4}</strong> awaiting host confirmation <span style={{ color: '#888' }}>(24h SLA)</span>
                        </Typography>
                    </Box> */}

                    {/* Badge 4: Date transfer overdue */}
                    <Box
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8,
                            bgcolor: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: '6px',
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FF9800' }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                            <strong>{attention.dateTransferOverdue ?? 1}</strong> date transfer overdue
                        </Typography>
                    </Box>

                    {/* Badge 5: Enquiries over 24h */}
                    <Box
                        onClick={() => navigate('/content-hub/enquiries')}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8,
                            bgcolor: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: '6px', cursor: 'pointer',
                            transition: 'all 0.2s', '&:hover': { borderColor: '#FFBD65', bgcolor: '#FFFDF9' }
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#E65100' }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                            <strong style={{ color: '#E65100' }}>{attention.enquiriesOver24h ?? 3}</strong> enquiries over 24h <span style={{ color: '#888' }}>(SLA breached)</span>
                        </Typography>
                    </Box>

                    {/* Badge 6: Enquiries past 1h */}
                    <Box
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8,
                            bgcolor: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: '6px',
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FF9800' }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                            <strong>{attention.enquiriesPast1h ?? 9}</strong> enquiries past 1h <span style={{ color: '#888' }}>(losing ranking points)</span>
                        </Typography>
                    </Box>

                    {/* Badge 7: Reviews to moderate */}
                    <Box
                        onClick={() => navigate('/review-management/reviews')}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8,
                            bgcolor: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: '6px', cursor: 'pointer',
                            transition: 'all 0.2s', '&:hover': { borderColor: '#FFBD65', bgcolor: '#FFFDF9' }
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4CAF50' }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                            <strong>{attention.reviewsToModerate ?? 3}</strong> reviews to moderate
                        </Typography>
                    </Box>

                    {/* Badge 8: Payouts due */}
                    {/* <Box
                        onClick={() => navigate('/payment-transactions')}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 2, py: 0.8,
                            bgcolor: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: '6px', cursor: 'pointer',
                            transition: 'all 0.2s', '&:hover': { borderColor: '#FFBD65', bgcolor: '#FFFDF9' }
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4CAF50' }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                            <strong style={{ color: '#4CAF50' }}>{attention.payoutsDue || "₹2.1L"}</strong> payouts due
                        </Typography>
                    </Box> */}
                </Box>
            </Paper>

            {/* Middle Section: GMV & Commission Chart + Booking States */}
            <Grid container spacing={2.5}>
                {/* GMV & Commission Bar Chart */}
                <Grid size={{ xs: 12, md: 7.5 }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#222' }}>
                                GMV & commission
                            </Typography>
                            <Link
                                onClick={() => navigate('/bookings')}
                                sx={{ color: '#FFBD65', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Open analytics →
                            </Link>
                        </Stack>

                        {/* Interactive Bar Chart Visualization */}
                        <Box sx={{ height: 210, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 0.5, px: 0.5, pt: 2, pb: 1 }}>
                            {(chartData.length > 0 ? chartData : [
                                { month: "Jan", gmv: 0, commission: 0 },
                                { month: "Feb", gmv: 0, commission: 0 },
                                { month: "Mar", gmv: 0, commission: 0 },
                                { month: "Apr", gmv: 0, commission: 0 },
                                { month: "May", gmv: 0, commission: 0 },
                                { month: "Jun", gmv: 0, commission: 0 },
                                { month: "Jul", gmv: 0, commission: 0 },
                                { month: "Aug", gmv: 0, commission: 0 },
                                { month: "Sep", gmv: 0, commission: 0 },
                                { month: "Oct", gmv: 0, commission: 0 },
                                { month: "Nov", gmv: 0, commission: 0 },
                                { month: "Dec", gmv: 0, commission: 0 }
                            ]).map((item, idx) => {
                                const gmvHeightPercent = item.gmv > 0 ? Math.max(Math.round((item.gmv / maxGmv) * 100), 6) : 0;
                                const commHeightPercent = item.commission > 0 ? Math.max(Math.round((item.commission / maxGmv) * 100), 4) : 0;

                                const formatVal = (val) => {
                                    if (!val || val === 0) return "₹0";
                                    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
                                    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
                                    return `₹${val.toFixed(0)}`;
                                };

                                return (
                                    <Box key={idx} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                                        <Tooltip title={`GMV: ${formatVal(item.gmv)} | Commission: ${formatVal(item.commission)}`} arrow>
                                            <Box sx={{ width: '100%', maxWidth: 36, display: 'flex', gap: '2px', alignItems: 'flex-end', height: '100%' }}>
                                                {/* GMV Bar */}
                                                <Box
                                                    sx={{
                                                        width: '60%',
                                                        height: `${gmvHeightPercent}%`,
                                                        bgcolor: item.gmv > 0 ? '#FFBD65' : '#E0E0E0',
                                                        minHeight: item.gmv > 0 ? '4px' : '2px',
                                                        borderRadius: '3px 3px 0 0',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': { bgcolor: item.gmv > 0 ? '#F5A93B' : '#BDBDBD' }
                                                    }}
                                                />
                                                {/* Commission Bar */}
                                                <Box
                                                    sx={{
                                                        width: '40%',
                                                        height: `${commHeightPercent}%`,
                                                        bgcolor: item.commission > 0 ? '#4CAF50' : '#E0E0E0',
                                                        minHeight: item.commission > 0 ? '4px' : '2px',
                                                        borderRadius: '3px 3px 0 0',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': { bgcolor: item.commission > 0 ? '#388E3C' : '#BDBDBD' }
                                                    }}
                                                />
                                            </Box>
                                        </Tooltip>
                                        <Typography sx={{ mt: 1, fontSize: '0.7rem', fontWeight: 500, color: '#666' }}>
                                            {item.month}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Chart Legend */}
                        <Stack direction="row" spacing={3} sx={{ mt: 2.5, pt: 1.5, borderTop: '1px solid #F0F0F0' }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#FFBD65' }} />
                                <Typography sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>GMV</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#4CAF50' }} />
                                <Typography sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>Commission (our revenue)</Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Booking States */}
                <Grid item size={{ xs: 12, md: 4.5 }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#222', mb: 2.5 }}>
                            Booking states
                        </Typography>

                        <Stack spacing={2.5}>
                            {/* Pending */}
                            <Box>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#555', fontWeight: 500 }}>Pending</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#222', fontWeight: 700 }}>
                                        {bookingStates.pending ?? 0}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={totalStateBookings > 0 ? Math.round(((bookingStates.pending ?? 0) / totalStateBookings) * 100) : 0}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: '#F0F0F0', '& .MuiLinearProgress-bar': { bgcolor: '#FF9800', borderRadius: 4 } }}
                                />
                            </Box>

                            {/* Confirmed */}
                            <Box>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#555', fontWeight: 500 }}>Confirmed</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#222', fontWeight: 700 }}>
                                        {bookingStates.confirmed ?? 0}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={totalStateBookings > 0 ? Math.round(((bookingStates.confirmed ?? 0) / totalStateBookings) * 100) : 0}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: '#F0F0F0', '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50', borderRadius: 4 } }}
                                />
                            </Box>

                            {/* Completed */}
                            <Box>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#555', fontWeight: 500 }}>Completed</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#222', fontWeight: 700 }}>
                                        {bookingStates.completed ?? 0}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={totalStateBookings > 0 ? Math.round(((bookingStates.completed ?? 0) / totalStateBookings) * 100) : 0}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: '#F0F0F0', '& .MuiLinearProgress-bar': { bgcolor: '#2196F3', borderRadius: 4 } }}
                                />
                            </Box>

                            {/* Cancelled */}
                            <Box>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#555', fontWeight: 500 }}>Cancelled</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#222', fontWeight: 700 }}>
                                        {bookingStates.cancelled ?? 0}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={totalStateBookings > 0 ? Math.round(((bookingStates.cancelled ?? 0) / totalStateBookings) * 100) : 0}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: '#F0F0F0', '& .MuiLinearProgress-bar': { bgcolor: '#F44336', borderRadius: 4 } }}
                                />
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            {/* Bottom Section: Latest Bookings + Top Retreats by Revenue (Side-by-side) */}
            <Grid container spacing={2.5} sx={{ mt: 2 }}>
                {/* Latest Bookings */}
                <Grid item size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#222' }}>
                                Latest bookings
                            </Typography>
                            <Link
                                onClick={() => navigate('/bookings')}
                                sx={{ color: '#8C6C58', fontSize: '0.8rem', fontWeight: 500, textDecoration: 'none', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            >
                                View all →
                            </Link>
                        </Stack>

                        {displayBookings.length > 0 ? (
                            <Stack spacing={1.5} divider={<Box sx={{ borderBottom: '1px solid #F5F5F5' }} />}>
                                {displayBookings.map((b, idx) => {
                                    const badge = getStatusBadge(b.bookingStatus);
                                    return (
                                        <Box key={idx} sx={{ py: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            {/* ID & User Name */}
                                            <Box sx={{ minWidth: 110 }}>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#222' }}>
                                                    {b.id}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>
                                                    {b.userName || 'Guest'}
                                                </Typography>
                                            </Box>

                                            {/* Retreat Name / Location */}
                                            <Box sx={{ flex: 1, px: 2 }}>
                                                <Typography sx={{ fontSize: '0.85rem', color: '#333', fontWeight: 500 }}>
                                                    {b.retreatTitle || b.retreatName || `Booking #${b.id}`}
                                                </Typography>
                                                {b.location && (
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>
                                                        {b.location}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Amount & Status Badge */}
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#222', whiteSpace: 'nowrap' }}>
                                                    {formatCurrencyDisplay(b.amount)}
                                                </Typography>
                                                <Box sx={{
                                                    px: 1.2, py: 0.4, borderRadius: '12px',
                                                    bgcolor: badge.bg, color: badge.color,
                                                    fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap',
                                                }}>
                                                    {badge.label}
                                                </Box>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <Typography sx={{ py: 4, textAlign: 'center', color: '#999', fontSize: '0.85rem' }}>
                                No recent bookings found.
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Top Retreats by Revenue */}
                <Grid item size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '8px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', height: '100%' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#222', mb: 2.5 }}>
                            Top retreats by revenue
                        </Typography>

                        {displayTopRetreats.length > 0 ? (
                            <Stack spacing={2.2}>
                                {displayTopRetreats.map((item, idx) => {
                                    const revenueVal = parseFloat(item.totalRevenue || 0);
                                    const percent = Math.round((revenueVal / safeMaxTopRevenue) * 100);
                                    const barColor = barColors[idx % barColors.length];

                                    return (
                                        <Box key={idx}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                                                <Typography sx={{ fontSize: '0.85rem', color: '#333', fontWeight: 500 }}>
                                                    {item.retreatName || item.title || `Retreat #${item.retreatId}`}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#222' }}>
                                                    {formatCurrencyDisplay(revenueVal)}
                                                </Typography>
                                            </Stack>
                                            <Box sx={{ width: '100%', height: 8, borderRadius: 4, bgcolor: '#F5EFE6', overflow: 'hidden' }}>
                                                <Box sx={{
                                                    height: '100%',
                                                    width: `${Math.max(percent, 4)}%`,
                                                    bgcolor: barColor,
                                                    borderRadius: 4,
                                                    transition: 'width 0.5s ease-in-out'
                                                }} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <Typography sx={{ py: 4, textAlign: 'center', color: '#999', fontSize: '0.85rem' }}>
                                No top revenue retreats found.
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
        </PermissionWrapper>
    );
};

export default RetreatDashboard;
